/**
 * Time-period primitives for the dashboard standards (PR 1 of 6).
 *
 * Every dashboard / KPI in Fuel Bench is anchored on a user-selected period:
 * Day / Week / MTD / QTD / YTD / LTD. This module defines:
 *   - the `Period` enum (canonical wire format),
 *   - `PERIOD_LABELS` for UI rendering,
 *   - `parsePeriod` for URL search-param round-tripping (with MTD/QTD aliases),
 *   - `periodWindows` for the date-math that downstream charts/KPIs consume.
 *
 * Anchor convention: callers pass an explicit `anchor: Date` (typically
 * `max(transaction_time)` from the warehouse, NOT `now()`, because the data
 * is intentionally stale). All bounds are returned as `Date` objects in UTC
 * to avoid TZ drift between the browser and the API.
 *
 * Comparison windows (the "vs LY same period" delta the user expects):
 *   - D  → prior calendar day
 *   - W  → prior calendar week (same Mon–Sun offset)
 *   - M  → same calendar month last year, clamped to anchor's day-of-month
 *   - Q  → same calendar quarter last year, clamped to anchor's day-of-quarter
 *   - YTD→ Jan 1 → anchor's day-of-year, last year
 *   - LTD→ no comparison (prior bounds are null)
 *
 * Used by `<TimePeriodSelector />` and (in PR 2+) by every KPI hook that
 * accepts a `period` query param.
 */

export type Period = 'D' | 'W' | 'M' | 'Q' | 'YTD' | 'LTD';

export const PERIOD_LABELS: Record<Period, string> = {
  D: 'Day',
  W: 'Week',
  M: 'MTD',
  Q: 'QTD',
  YTD: 'YTD',
  LTD: 'LTD',
};

export const PERIODS: Period[] = ['D', 'W', 'M', 'Q', 'YTD', 'LTD'];

/**
 * Parse a raw URL search-param value into a `Period`. Accepts the canonical
 * single-letter codes plus the `MTD` / `QTD` aliases for back-compat with
 * older deep links and Superset embeds.
 */
export function parsePeriod(raw: string | null, fallback: Period = 'M'): Period {
  if (raw && (PERIODS as string[]).includes(raw)) return raw as Period;
  if (raw === 'MTD') return 'M';
  if (raw === 'QTD') return 'Q';
  return fallback;
}

export interface PeriodWindow {
  /** Inclusive lower bound (UTC, 00:00:00.000 of the period-start day). */
  since: Date;
  /** Inclusive upper bound (UTC, 23:59:59.999 of the anchor day). */
  until: Date;
  /** Comparison window lower bound, or `null` for LTD (no comparison). */
  priorSince: Date | null;
  /** Comparison window upper bound, or `null` for LTD. */
  priorUntil: Date | null;
  /** Human label for the headline period, e.g. "Feb 2026 MTD". */
  label: string;
  /** Human label for the comparison period, or `null` for LTD. */
  priorLabel: string | null;
}

/** LTD origin — matches the warehouse `fuel.transaction` data start. */
const LTD_START = new Date(Date.UTC(2021, 0, 1, 0, 0, 0, 0));

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** Build a UTC `Date` at 00:00:00.000 of the given Y/M/D. */
function utcDay(year: number, monthIdx: number, day: number): Date {
  return new Date(Date.UTC(year, monthIdx, day, 0, 0, 0, 0));
}

/** Build a UTC `Date` at 23:59:59.999 of the given Y/M/D. */
function utcEndOfDay(year: number, monthIdx: number, day: number): Date {
  return new Date(Date.UTC(year, monthIdx, day, 23, 59, 59, 999));
}

/** Last day of a given UTC month (handles leap years). */
function lastDayOfMonth(year: number, monthIdx: number): number {
  // Day 0 of the next month = last day of the requested month.
  return new Date(Date.UTC(year, monthIdx + 1, 0)).getUTCDate();
}

/** Clamp `day` to the actual last day of (year, monthIdx). */
function clampDay(year: number, monthIdx: number, day: number): number {
  return Math.min(day, lastDayOfMonth(year, monthIdx));
}

/** Quarter index 0..3 for a given UTC month. */
function quarterOf(monthIdx: number): number {
  return Math.floor(monthIdx / 3);
}

/**
 * Compute (since, until, prior_since, prior_until, labels) for the given
 * `period` anchored on `anchor`. Pure function — no I/O, no `now()` reads.
 *
 * Edge cases handled:
 *   - Anchor's `until` is end-of-day (23:59:59.999) — inclusive on both ends.
 *   - Feb 29 leap-year clamp: when prior-year period would land on Feb 29
 *     in a non-leap year, day-of-month is clamped to Feb 28.
 *   - LTD has `priorSince`/`priorUntil` = null (no comparison defined).
 *   - All Date objects are UTC-anchored; callers must format for display.
 */
export function periodWindows(period: Period, anchor: Date): PeriodWindow {
  const ay = anchor.getUTCFullYear();
  const am = anchor.getUTCMonth();
  const ad = anchor.getUTCDate();

  const until = utcEndOfDay(ay, am, ad);

  switch (period) {
    case 'D': {
      const since = utcDay(ay, am, ad);
      const priorAnchor = new Date(since.getTime());
      priorAnchor.setUTCDate(priorAnchor.getUTCDate() - 1);
      const py = priorAnchor.getUTCFullYear();
      const pm = priorAnchor.getUTCMonth();
      const pd = priorAnchor.getUTCDate();
      return {
        since,
        until,
        priorSince: utcDay(py, pm, pd),
        priorUntil: utcEndOfDay(py, pm, pd),
        label: `${ad} ${MONTH_NAMES[am]} ${ay} (Day)`,
        priorLabel: `${pd} ${MONTH_NAMES[pm]} ${py}`,
      };
    }

    case 'W': {
      // ISO-style: week starts Monday. JS getUTCDay() → 0=Sun..6=Sat.
      const dow = anchor.getUTCDay();
      const offsetToMonday = dow === 0 ? 6 : dow - 1;
      const monday = utcDay(ay, am, ad - offsetToMonday);
      const priorMonday = new Date(monday.getTime());
      priorMonday.setUTCDate(priorMonday.getUTCDate() - 7);
      const priorUntilDate = new Date(until.getTime());
      priorUntilDate.setUTCDate(priorUntilDate.getUTCDate() - 7);
      return {
        since: monday,
        until,
        priorSince: priorMonday,
        priorUntil: priorUntilDate,
        label: `Week of ${monday.getUTCDate()} ${MONTH_NAMES[monday.getUTCMonth()]} ${monday.getUTCFullYear()}`,
        priorLabel: `Week of ${priorMonday.getUTCDate()} ${MONTH_NAMES[priorMonday.getUTCMonth()]} ${priorMonday.getUTCFullYear()}`,
      };
    }

    case 'M': {
      const since = utcDay(ay, am, 1);
      const priorYear = ay - 1;
      const priorDay = clampDay(priorYear, am, ad);
      return {
        since,
        until,
        priorSince: utcDay(priorYear, am, 1),
        priorUntil: utcEndOfDay(priorYear, am, priorDay),
        label: `${MONTH_NAMES[am]} ${ay} MTD`,
        priorLabel: `${MONTH_NAMES[am]} ${priorYear} MTD`,
      };
    }

    case 'Q': {
      const q = quarterOf(am);
      const qStartMonth = q * 3;
      const since = utcDay(ay, qStartMonth, 1);
      const priorYear = ay - 1;
      const priorDay = clampDay(priorYear, am, ad);
      return {
        since,
        until,
        priorSince: utcDay(priorYear, qStartMonth, 1),
        priorUntil: utcEndOfDay(priorYear, am, priorDay),
        label: `Q${q + 1} ${ay} QTD`,
        priorLabel: `Q${q + 1} ${priorYear} QTD`,
      };
    }

    case 'YTD': {
      const since = utcDay(ay, 0, 1);
      const priorYear = ay - 1;
      const priorDay = clampDay(priorYear, am, ad);
      return {
        since,
        until,
        priorSince: utcDay(priorYear, 0, 1),
        priorUntil: utcEndOfDay(priorYear, am, priorDay),
        label: `YTD ${ay}`,
        priorLabel: `YTD ${priorYear}`,
      };
    }

    case 'LTD': {
      return {
        since: LTD_START,
        until,
        priorSince: null,
        priorUntil: null,
        label: 'LTD',
        priorLabel: null,
      };
    }
  }
}
